import { mutationOptions } from '@tanstack/react-query';
import { authBaseURL } from '../../constants/hosts';
import * as users from '#proto/index.qonto.account_management.users.firstparty.v2';
import * as usersV5 from '#proto/index.qonto.account_management.users.firstparty.v5';
import { client } from '#queries/utils';
import { ApiUpdateUserRequest, type UpdateUserParams } from './types';

/**
 * @deprecated
 * Types are not based on proto definitions
 * Requires a full representation of user because it's a PUT
 * Requires userId although we only ever update the current user
 * Prefer using updateMe which is based on proto definitions and doesn't have these issues
 */
function updateUser() {
  return mutationOptions({
    mutationFn: async ({ userId, user }: UpdateUserParams) => {
      if (!userId) throw new Error('No user id');

      const result = await client(`${authBaseURL}/v2/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user }),
      });

      return users.ApiGetUserResponse.fromJSON(result);
    },
  });
}

export async function updatePhoneNumber(phoneNumber: string): Promise<void> {
  await client(`${authBaseURL}/v2/users/me/phone_number`, {
    method: 'POST',
    body: {
      user: {
        phone_number: phoneNumber,
      },
    },
  });
}

function updatePhoneNumberMutation() {
  return mutationOptions({
    mutationFn: ({ phoneNumber }: { phoneNumber: string }) => updatePhoneNumber(phoneNumber),
  });
}

export const usersMutations = {
  createUser: mutationOptions({
    mutationFn: async (data: users.ApiCreateUserRequest) => {
      const result = await client(`${authBaseURL}/v2/users`, {
        method: 'POST',
        body: JSON.stringify(users.ApiCreateUserRequest.toJSON(data)),
      });

      return users.ApiCreateUserResponse.fromJSON(result);
    },
  }),
  /**
   * Sends or resends a one-time confirmation code to the user's email.
   * Mirrors the legacy Ember `User#confirmEmail()` model method.
   * `firstName`, `lastName`, and `locale` are optional — include them when
   * available for personalisation (e.g. from `EmailConfirmForm` in CRK).
   */
  confirmEmail: mutationOptions({
    mutationFn: async ({
      email,
      firstName,
      lastName,
      locale,
    }: {
      email: string;
      firstName?: string | null;
      lastName?: string | null;
      locale?: string | null;
    }) => {
      await client(`${authBaseURL}/v2/users/confirm`, {
        method: 'POST',
        body: JSON.stringify({
          user: {
            email,
            first_name: firstName,
            last_name: lastName,
            locale,
          },
        }),
      });
    },
  }),
  updateUser,
  updateMe: mutationOptions({
    mutationFn: async (data: ApiUpdateUserRequest) => {
      const result = await client(`${authBaseURL}/v2/users/me`, {
        method: 'PATCH',
        body: JSON.stringify(ApiUpdateUserRequest.toJSON(data)),
      });

      return users.ApiGetUserResponse.fromJSON(result);
    },
  }),
  updatePhoneNumber: updatePhoneNumberMutation,
  addPhoneNumber: mutationOptions({
    mutationFn: async (data: users.ApiCreatePhoneNumberRequest) => {
      const result = await client(`${authBaseURL}/v2/users/me/phone_number`, {
        method: 'POST',
        body: JSON.stringify(users.ApiCreatePhoneNumberRequest.toJSON(data)),
      });

      return users.ApiCreatePhoneNumberResponse.fromJSON(result);
    },
  }),
  updatePhoneNumberV5: mutationOptions({
    mutationFn: async (data: usersV5.ApiUpdatePhoneNumberRequest) => {
      await client(`${authBaseURL}/v5/user/phone_number`, {
        method: 'PUT',
        body: JSON.stringify(usersV5.ApiUpdatePhoneNumberRequest.toJSON(data)),
      });
    },
  }),
  sendEmailOtp: mutationOptions({
    mutationFn: async (data: usersV5.ApiSendEmailOtpRequest) => {
      await client(`${authBaseURL}/v5/user/email_update/send_otp`, {
        method: 'POST',
        body: JSON.stringify(usersV5.ApiSendEmailOtpRequest.toJSON(data)),
      });
    },
  }),
  validateNewPhoneOtp: mutationOptions({
    mutationFn: async (data: usersV5.ApiValidateNewPhoneOtpRequest) => {
      await client(`${authBaseURL}/v5/user/phone_number/validate_new_phone_otp`, {
        method: 'POST',
        body: JSON.stringify(usersV5.ApiValidateNewPhoneOtpRequest.toJSON(data)),
      });
    },
  }),
  getPhoneMfaOptions: mutationOptions({
    mutationFn: async () => {
      const result = await client(`${authBaseURL}/v5/user/phone_number/mfa_options`);
      return usersV5.ApiGetPhoneMfaOptionsResponse.fromJSON(result);
    },
  }),
  activatePhoneNumber: mutationOptions({
    mutationFn: async (data: usersV5.ApiActivatePhoneNumberRequest) => {
      await client(`${authBaseURL}/v5/user/phone_number/activate`, {
        method: 'POST',
        body: JSON.stringify(usersV5.ApiActivatePhoneNumberRequest.toJSON(data)),
      });
    },
  }),
};
